import React from 'react';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';

export default function CartDrawer() {
  const { state, closeCart, removeItem, updateQuantity, subtotal, getItemKey } = useCart();
  const navigate = useNavigate();

  if (!state.isOpen) return null;

  const shipping = subtotal >= 10000 ? 0 : 250;
  const total = subtotal + shipping;

  const formatPrice = (price: number) => `Rs.${price.toLocaleString()}`;

  return (
    <>
      {/* Translucent backdrop click handler */}
      <div className="fixed inset-0 bg-primary/40 backdrop-blur-xs z-50 transition-opacity duration-300" onClick={closeCart} />
      
      {/* Side panel */}
      <div 
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white border-l border-gray-100 z-50 flex flex-col shadow-premium rounded-l-3xl overflow-hidden" 
        style={{ animation: 'slideInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-serif font-extrabold text-primary">Your Shopping Cart</h2>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">
              {state.items.reduce((s, i) => s + i.quantity, 0)} Items Selected
            </p>
          </div>
          <button onClick={closeCart} aria-label="Close cart" className="p-2 text-gray-400 hover:text-primary hover:bg-gray-50 rounded-xl transition-all">
            <X size={20} />
          </button>
        </div>

        {/* Free shipping reward status */}
        {subtotal > 0 && subtotal < 10000 && (
          <div className="px-6 py-4 bg-accent/5 border-b border-accent/10 flex-shrink-0">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-primary font-semibold">Add <strong className="text-accent">{formatPrice(10000 - subtotal)}</strong> more for FREE delivery</span>
              <span className="text-accent font-bold">{Math.round((subtotal / 10000) * 100)}%</span>
            </div>
            <div className="h-2 bg-accent/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-accent to-accent/80 rounded-full transition-all duration-500" 
                style={{ width: `${Math.min((subtotal / 10000) * 100, 100)}%` }} 
              />
            </div>
          </div>
        )}
        {subtotal >= 10000 && (
          <div className="px-6 py-3.5 bg-green-50 border-b border-green-150/50 flex items-center gap-2 flex-shrink-0">
            <span className="text-green-600 text-sm">🎉</span>
            <span className="text-green-700 text-xs font-bold uppercase tracking-wide">You have unlocked FREE climate-controlled transit!</span>
          </div>
        )}

        {/* Cart Item Feed */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {state.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-10">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-300">
                <ShoppingBag size={28} />
              </div>
              <p className="text-primary font-bold text-sm">Your cart is empty</p>
              <p className="text-gray-400 text-xs mt-1.5 max-w-[200px] mx-auto">Explore our lovable pets and premium supplies to add items.</p>
              <button 
                onClick={closeCart} 
                className="mt-6 bg-primary hover:bg-accent text-white px-8 py-3 text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-md hover:scale-105 active:scale-95"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {state.items.map(item => {
                const key = getItemKey(item);
                return (
                  <div key={key} className="flex gap-4 py-4.5 first:pt-0 last:pb-0">
                    {/* Item Thumbnail */}
                    <div className="w-20 h-24 bg-gray-50 rounded-2xl overflow-hidden flex-shrink-0 border border-gray-100">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    {/* Item Info details */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                      <div>
                        <h3 className="text-xs font-bold text-primary leading-tight truncate">{item.name}</h3>
                        {(item.size || item.color) && (
                          <p className="text-[10px] font-semibold text-gray-400 mt-1 uppercase tracking-wider">
                            {item.size && `Size: ${item.size}`}
                            {item.size && item.color && ' • '}
                            {item.color && `Color: ${item.color}`}
                          </p>
                        )}
                        <p className="text-xs font-bold text-accent mt-1.5">{formatPrice(item.price)}</p>
                      </div>
                      
                      {/* Quantity Selector buttons */}
                      <div className="flex items-center gap-1.5 mt-2">
                        <div className="flex items-center bg-gray-50 border border-gray-100 rounded-xl px-1">
                          <button
                            aria-label="Decrease quantity"
                            onClick={() => updateQuantity(key, item.quantity - 1)}
                            className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-primary transition-colors active:scale-75"
                          >
                            <Minus size={11} />
                          </button>
                          <span className="text-xs w-7 text-center font-bold text-primary">{item.quantity}</span>
                          <button
                            aria-label="Increase quantity"
                            onClick={() => updateQuantity(key, item.quantity + 1)}
                            className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-primary transition-colors active:scale-75"
                          >
                            <Plus size={11} />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Item Subtotal & Delete Action */}
                    <div className="flex flex-col items-end justify-between py-0.5 flex-shrink-0">
                      <button 
                        aria-label="Remove item" 
                        onClick={() => removeItem(key)} 
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <X size={15} />
                      </button>
                      <span className="text-xs font-extrabold text-primary">{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Checkout Summary */}
        {state.items.length > 0 && (
          <div className="px-6 py-6 border-t border-gray-100 bg-[#FCFAF7] flex-shrink-0 space-y-4">
            <div className="space-y-2.5">
              <div className="flex justify-between text-xs font-medium text-gray-500 uppercase tracking-wider">
                <span>Subtotal</span>
                <span className="font-extrabold text-primary">{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-xs font-medium text-gray-500 uppercase tracking-wider">
                <span>Transit Clearance</span>
                <span className={shipping === 0 ? 'text-green-600 font-bold' : 'font-extrabold text-primary'}>
                  {shipping === 0 ? 'FREE' : formatPrice(shipping)}
                </span>
              </div>
              {shipping > 0 && (
                <p className="text-[10px] text-gray-450 italic">Unlocks free transport above Rs.10,000</p>
              )}
              <div className="flex justify-between text-sm font-extrabold text-primary border-t border-gray-150 pt-3.5 uppercase tracking-wide">
                <span>Estimated Total</span>
                <span className="text-base font-extrabold text-primary">{formatPrice(total)}</span>
              </div>
            </div>
            
            <div className="space-y-2 pt-2">
              <button
                onClick={() => { closeCart(); navigate('/checkout'); }}
                className="w-full bg-primary hover:bg-accent text-white py-3.5 text-[11px] font-bold uppercase tracking-widest transition-all rounded-xl shadow-premium hover:-translate-y-0.5 active:scale-98 flex items-center justify-center gap-2"
              >
                Proceed to Checkout
              </button>
              <button 
                onClick={closeCart} 
                className="w-full border-2 border-gray-200 text-gray-700 py-3 text-[10px] font-bold uppercase tracking-widest hover:border-primary hover:text-primary transition-all rounded-xl active:scale-98"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
